<template>
    <Head title="Register" />

    <div class="relative lg:fixed h-full shrink-0 justify-center md:px-12 lg:px-0 bg-white">
        <div class="relative z-10 flex flex-1 flex-col h-full bg-white px-4 py-10 shadow-2xl md:flex-none md:px-28 overflow-y-scroll overflow-x-hidden scrollbar-hide">
            <jet-authentication-card class="">
                <div class="flex justify-center items-center">
                    <jet-authentication-card-logo-colored />
                </div>

                <h1 class="text-lg font-semibold text-gray-900 text-center mt-5">
                    Register for a free account
                </h1>
                <jet-validation-errors class="mb-4" />
                <form @submit.prevent="submit" aria-label="Register Form" class="mt-10 grid grid-cols-1 gap-y-6">

                    <div class="">
                        <jet-label for="account_type" value="Account Type" />
                        <div class="mt-2 relative">
                            <fieldset>
                                <legend class="sr-only">Account Type</legend>
                                <div v-if="this.type === 'agent' || this.type === 'landlord' || this.type === 'affiliate.register' || this.type === 'affiliate.catchall'">
                                    <div class="space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10">
                                        <div class="flex items-center">
                                            <input id="agent" type="radio" name="account_type" value="agent" v-model="form.account_type" required class="hidden focus:ring-rent-risk-blue-400 h-4 w-4 text-rent-risk-blue-400 border-gray-300" />
                                            <label for="agent" class="block text-sm font-bold text-gray-900">
                                                <span v-if="this.type === 'affiliate.register' || this.type === 'affiliate.catchall'">Agent/Landlord/Property Owner</span>
                                                <span v-else>Agent/Landlord/Property Owner</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10 mt-2">
                                    <div class="flex items-center">
                                        <input id="tenant" type="radio" name="account_type" value="applicant" v-model="form.account_type" required class="hidden focus:ring-rent-risk-blue-400 h-4 w-4 text-rent-risk-blue-400 border-gray-300" />
                                        <label for="tenant" class="block text-sm font-bold text-gray-900">
                                            Tenant/Applicant
                                        </label>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                    </div>

                    <div class="">
                        <jet-label for="name" value="Name" class="required-input"/>
                        <jet-input id="name" type="text" class="mt-1 block w-full" v-model="form.name" required autofocus autocomplete="name" />
                        <p v-text="form.errors.name" class="text-red-500 text-sm"></p>
                    </div>

                    <div class="" v-if="form.account_type === 'agent' || form.account_type === 'affiliate.register' || form.account_type === 'affiliate.catchall'">
                        <jet-label for="brokerage_name" value="Brokerage Name (if applicable)" />
                        <jet-input id="brokerage_name" type="text" class="mt-1 block w-full" v-model="form.brokerage_name" />
                    </div>

                    <div class="">
                        <jet-label for="email" value="Email" class="required-input" />
                        <jet-input id="email" type="email" class="mt-1 block w-full" v-model="form.email" :disabled="invitation" :class="invitation ? 'bg-gray-200' : ''" required />
                        <p v-text="form.errors.email" class="text-red-500 text-sm"></p>
                    </div>

                    <div v-if="this.form.account_type === 'agent'" class="gap-y-6">
                        <div class="">
                            <jet-label for="description" value="What best describes you?" class="required-input" />
                            <select id='late_charges_type' v-model="form.description" required
                                    class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm block w-full mt-1"
                            >
                                <option v-for="(description, index) in descriptions" :key="index">{{ description }}</option>
                            </select>
                            <p v-text="form.errors.description"
                               class="text-red-500 text-sm"></p>
                        </div>
                        <div class="mt-5" v-if="form.description === 'Other'">
                            <jet-label for="other_description_specify" value="Please Specify" class="required-input"/>
                            <jet-input id="other_description_specify" type="text" class="mt-1 block w-full" v-model="form.other_description_specify" />
                            <p v-text="form.errors.other_description_specify" class="text-red-500 text-sm"></p>
                        </div>
                        <div v-if="!form.not_in_the_list" class="mt-5">
                            <Combobox as="div" v-model="selectedCompany" @update:modelValue="query = ''">
                                <div class="flex justify-between">
                                    <ComboboxLabel class="block text-sm font-medium leading-6 text-gray-900 required-input">What company are you associated with?</ComboboxLabel>
                                </div>
                                <div class="relative mt-2">
                                    <ComboboxInput
                                        required
                                        class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm block w-full"
                                        @change="query = $event.target.value" @blur="query = ''"
                                        :display-value="(company) => company?.name"/>
                                    <ComboboxButton
                                        class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                                        <font-awesome-icon :icon="faChevronDown" class="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                    </ComboboxButton>

                                    <ComboboxOptions v-if="filteredCompanies.length > 0"
                                                     class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                                    >
                                        <ComboboxOption v-for="company in filteredCompanies" :key="company.id" :value="company"
                                                        as="template"
                                                        v-slot="{ active, selected }">
                                            <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-indigo-600 text-white' : 'text-gray-900']">
                          <span :class="['block truncate', selected && 'font-semibold']">
                          {{ company.name }}
                          </span>

                                                <span v-if="selected"
                                                      :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600']">
                          <font-awesome-icon :icon="faCheck" class="h-5 w-5" aria-hidden="true"/>
                          </span>
                                            </li>
                                        </ComboboxOption>
                                    </ComboboxOptions>
                                </div>
                            </Combobox>
                            <p v-text="form.errors.company" class="text-red-500 text-sm"></p>
                        </div>
                        <div v-if="form.not_in_the_list" class="mt-4">
                            <div class="flex justify-between">
                                <label for="specify_company" class="block text-sm font-medium text-gray-700">
                                    Type your company name
                                </label>
                            </div>
                            <div class="mt-1 relative rounded-md">
                                <input id="specify_company" v-model="form.specify_company" type="text"
                                       class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm block w-full"
                                >
                                <p v-text="form.errors.specify_company" class="text-red-500 text-sm"></p>
                            </div>
                        </div>
                        <button v-if="form.not_in_the_list" type="button" @click="form.not_in_the_list = false;" class="text-sm text-blue-600">Select from the list?</button>
                        <button v-else type="button" @click="form.not_in_the_list = true;" class="text-sm text-blue-600">Company not on the list?</button>

                    </div>

                    <div class="">
                        <jet-label for="password" value="Password" class="required-input" tooltip="The password must be at least 10 characters and contain at least one uppercase character, one number, and one special character." />
                        <div class="relative mt-1 flex items-center">
                            <input :type="isShowPassword ? 'text' : 'password'" id="password" v-model="form.password" required class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm block w-full" />
                            <div class="absolute inset-y-0 right-0 flex py-1.5 pr-1.5">
                                <button type="button" @click="isShowPassword = !isShowPassword" class="inline-flex items-center rounded border border-gray-200 px-1 font-sans text-xs" :class="isShowPassword ? 'text-gray-500' : 'text-blue-600'"><font-awesome-icon icon="fa-eye" /></button>
                            </div>
                        </div>
                        <p v-text="form.errors.password" class="text-red-500 text-sm"></p>
                    </div>

                    <div class="">
                        <jet-label for="password_confirmation" value="Confirm Password" class="required-input" />
                        <jet-input id="password_confirmation" :type="isShowPassword ? 'text' : 'password'" class="mt-1 block w-full" v-model="form.password_confirmation" required autocomplete="new-password" />
                    </div>

                    <div class="" v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature">
                        <jet-label for="terms">
                            <div class="flex items-center">
                                <jet-checkbox name="terms" id="terms" v-model:checked="form.terms" />

                                <div class="ml-2">
                                    By creating an account, you agree to our <a target="_blank" :href="route('terms-and-conditions')" class="underline text-sm text-gray-600 hover:text-gray-900">Terms of Service</a> and <a target="_blank" :href="route('privacy-policy')" class="underline text-sm text-gray-600 hover:text-gray-900">Privacy Policy</a>
                                </div>
                            </div>
                        </jet-label>
                    </div>

                    <div class="" v-if="form.account_type === 'applicant' && 1 === 3">
                        <h3 class="text-center text-lg font-bold leading-7 text-gray-700 sm:text-2xl sm:truncate">Renter Report Share</h3>
                        <p class="text-sm text-gray-600">
                            You understand that by clicking on the "I Agree" button immediately following this notice, you are providing "written instructions" to RentRisk
                             under the Fair Credit Reporting Act authorizing RentRisk to obtain information from your personal credit report or other information from Experian.
                            You authorize RentRisk to obtain such information to confirm your identity, access and share your credit report and Vantage Score credit score with an
                            end user whom you have authorized to view your report and score for tenant screening purposes.
                        </p>
                        <jet-label for="renter_report_share">
                            <div class="flex items-center">
                                <jet-checkbox name="renter_report_share" id="renter_report_share" v-model:checked="form.renter_report_share" />
                                <div class="ml-2">I Agree</div>
                            </div>
                        </jet-label>
                    </div>

                    <div class="flex items-center justify-between mt-4">
                        <a target="_blank" :href="appUrl('/RentRisk-Account-Creation-Property-Setup-Instructions.pdf')"
                           class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-800 disabled:bg-purple-300 hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
                            Need Help?
                        </a>
                        <jet-button class="ml-0 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-rent-risk-blue-400 disabled:bg-indigo-300 hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 bg-rent-risk-blue-400 hover:bg-black text-gray-900" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                            Register
                        </jet-button>

                    </div>
                    <div class="">
                        <Link :href="route('login')" class="underline text-sm text-gray-600 hover:text-gray-900">
                            Already registered?
                        </Link>
                    </div>
                </form>
            </jet-authentication-card>
        </div>
    </div>
    <div class="hidden md:flex lg:pl-72 h-screen rentrisk-sidebar-gradient"></div>
</template>

<script>
    import JetAuthenticationCard from '@/Jetstream/AuthenticationCard.vue'
    import JetAuthenticationCardLogo from '@/Jetstream/AuthenticationCardLogo.vue'
    import JetAuthenticationCardLogoColored from '@/Jetstream/AuthenticationCardLogoColored.vue'
    import JetButton from '@/Jetstream/Button.vue'
    import Button from '@/Jetstream/Button.vue'
    import JetInput from '@/Jetstream/Input.vue'
    import JetCheckbox from '@/Jetstream/Checkbox.vue'
    import JetLabel from '@/Jetstream/Label.vue'
    import JetValidationErrors from '@/Jetstream/ValidationErrorsWithHtml.vue'
    import { Head, Link } from '@inertiajs/vue3'
    import RadioGroup from '@/Shared/RadioGroup.vue'
    import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
    import { faCheck, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
    import {
        Combobox,
        ComboboxButton,
        ComboboxInput,
        ComboboxLabel,
        ComboboxOption,
        ComboboxOptions
    } from "@headlessui/vue";

    export default {
        components: {
            Head,
            JetAuthenticationCard,
            JetAuthenticationCardLogo,
            JetAuthenticationCardLogoColored,
            JetButton,
            JetInput,
            JetCheckbox,
            JetLabel,
            JetValidationErrors,
            Link,
            RadioGroup,
            Button,
            ComboboxLabel,
            ComboboxInput,
            ComboboxOptions,
            ComboboxOption,
            ComboboxButton,
            Combobox,
            FontAwesomeIcon,
        },

        props: {
            type: String,
            affiliate_share_hash: String,
            invitation: Object,
            companies: Object,
            descriptions: Object,
            field_name: String,
            field_value: String,
        },

        data() {
            return {
                form: this.$inertia.form({
                    account_type: (this.type === 'applicant') ? 'applicant' : 'agent',
                    name: '',
                    affiliate: (this.type === 'affiliate') ? 'affiliate' : '',
                    affiliate_share_hash: this.affiliate_share_hash,
                    email: this.invitation ? this.invitation.email : null,
                    password: '',
                    password_confirmation: '',
                    terms: false,
                    renter_report_share: false,
                    invitation_token: this.invitation ? this.invitation.invitation_token : null,
                    description: null,
                    other_description_specify: null,
                    company: null,
                    specify_company: null,
                    not_in_the_list: false,
                    field_name: this.field_name,
                    field_value: this.field_value,
                }),
                query: '',
                selectedCompany: null,
                isShowPassword: false,
                faCheck,
                faChevronDown,
            }
        },

        computed: {
            filteredCompanies() {
                if (this.query === '') {
                    return this.companies;
                }
                return this.companies.filter((company) => {
                    return company.name.toLowerCase().includes(this.query.toLowerCase());
                });
            }
        },

        watch: {
            selectedCompany(newValue) {
                if (newValue) {
                    this.form.company = newValue.id;
                }
            }
        },

        methods: {
            submit() {
                this.form.post(this.route('register'), {
                    onFinish: () => this.form.reset('password', 'password_confirmation'),
                })
            },
        }
    }
</script>
